import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { uploadImageToCloudinary, uploadBase64ImageToCloudinary } from '../services/cloudinaryService';

// Education data interface
export interface Education {
    id?: string;
    institution: string;
    degree: string;
    fieldOfStudy?: string;
    location?: string;
    startDate: string; // Format: "YYYY-MM" or "YYYY-MM-DD"
    endDate?: string;  // Format: "YYYY-MM" or "YYYY-MM-DD", null means "Present"
    grade?: string;
    descriptions?: string[];
    institutionLogo?: string;
    website?: string;
    achievements?: string[];
    order?: number;
    createdAt?: Timestamp;
    updatedAt?: Timestamp;
}

// Helper function to format date ranges
export const formatDateRange = (startDate: string, endDate?: string): string => {
    // Format date as "MMM YYYY" or handle if only year is provided
    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr + (dateStr.length === 4 ? '-01-01' : dateStr.length === 7 ? '-01' : ''));
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();

        return dateStr.length === 4 ? year.toString() : `${month} ${year}`;
    };

    const start = formatDate(startDate);
    const end = endDate ? formatDate(endDate) : 'Present';

    return `${start} - ${end}`;
};

// Collection reference
const educationCollection = collection(db, 'education');

/**
 * Get all education entries
 */
export const getAllEducation = async (): Promise<Education[]> => {
    try {
        const educationQuery = query(educationCollection, orderBy('order', 'desc'));
        const querySnapshot = await getDocs(educationQuery);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Education));
    } catch (error) {
        console.error('Error getting education entries:', error);
        throw error;
    }
};

/**
 * Get an education entry by ID
 */
export const getEducationById = async (id: string): Promise<Education | null> => {
    try {
        const docRef = doc(db, 'education', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as Education;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting education entry:', error);
        throw error;
    }
};

/**
 * Create a new education entry
 */
export const createEducation = async (educationData: Omit<Education, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        // Handle logo upload if provided
        let logoUrl = educationData.institutionLogo;
        if (logoUrl && logoUrl.startsWith('data:')) {
            logoUrl = await uploadBase64ImageToCloudinary(logoUrl, 'education_logos');
        }

        // Get current max order and add 1
        const education = await getAllEducation();
        const maxOrder = education.length > 0
            ? Math.max(...education.map(edu => edu.order || 0))
            : -1;

        const docRef = await addDoc(educationCollection, {
            ...educationData,
            institutionLogo: logoUrl,
            order: maxOrder + 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        return docRef.id;
    } catch (error) {
        console.error('Error creating education entry:', error);
        throw error;
    }
};

/**
 * Update an education entry
 */
export const updateEducation = async (id: string, educationData: Partial<Omit<Education, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
    try {
        // Handle logo upload if provided
        let logoUrl = educationData.institutionLogo;
        if (logoUrl && logoUrl.startsWith('data:')) {
            logoUrl = await uploadBase64ImageToCloudinary(logoUrl, 'education_logos');
            educationData.institutionLogo = logoUrl;
        }

        const docRef = doc(db, 'education', id);
        await updateDoc(docRef, {
            ...educationData,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating education entry:', error);
        throw error;
    }
};

/**
 * Delete an education entry
 */
export const deleteEducation = async (id: string): Promise<void> => {
    try {
        const docRef = doc(db, 'education', id);
        await deleteDoc(docRef);

        // Re-order remaining entries
        await reorderEducation();
    } catch (error) {
        console.error('Error deleting education entry:', error);
        throw error;
    }
};

/**
 * Reorder education entries after deletion
 */
export const reorderEducation = async (): Promise<void> => {
    try {
        const education = await getAllEducation();

        // Update order for each entry
        const updatePromises = education.map((edu, index) => {
            if (edu.id && edu.order !== index) {
                const docRef = doc(db, 'education', edu.id);
                return updateDoc(docRef, { order: index });
            }
            return Promise.resolve();
        });

        await Promise.all(updatePromises);
    } catch (error) {
        console.error('Error reordering education entries:', error);
        throw error;
    }
};

/**
 * Update the order of education entries
 */
export const updateEducationOrder = async (reorderedEducation: { id: string; order: number }[]): Promise<void> => {
    try {
        const updatePromises = reorderedEducation.map(item => {
            const docRef = doc(db, 'education', item.id);
            return updateDoc(docRef, { order: item.order });
        });

        await Promise.all(updatePromises);
    } catch (error) {
        console.error('Error updating education order:', error);
        throw error;
    }
}; 